import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PageHeader } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, LogOut, ArrowLeft, Trash2, CalendarOff } from "lucide-react";

function HolidaySection({ isAdmin }) {
  const [holidays, setHolidays] = useState(null);
  const [form, setForm] = useState({ date: "", name: "" });
  const [country, setCountry] = useState("US");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [importing, setImporting] = useState(false);

  const importCountry = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/holidays/import", { country, year: parseInt(year, 10) });
      setHolidays(data.holidays);
      toast.success(`${data.added} holiday${data.added === 1 ? "" : "s"} imported for ${country} ${year}`);
    } catch (e) { toast.error(errMsg(e)); }
    setImporting(false);
  };

  const load = () => api.get("/holidays").then((r) => setHolidays(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      const { data } = await api.post("/holidays", form);
      setHolidays(data); setForm({ date: "", name: "" });
      toast.success("Holiday added — scheduling will skip this date");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (h) => {
    try { await api.delete(`/holidays/${h.date}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-10" data-testid="holiday-section">
      <div className="flex items-center gap-2 mb-1">
        <CalendarOff className="h-4 w-4 text-[#1E6FE8]" />
        <h2 className="font-display font-bold text-xl tracking-tight">Public Holiday Calendar</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Working-day scheduling (Gantt, critical path, scenarios) skips these dates just like weekends — for every project in your organisation.</p>
      {isAdmin && (
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div><Label className="text-xs">Date</Label><Input data-testid="holiday-date-input" type="date" className="h-9 w-40" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div><Label className="text-xs">Name</Label><Input data-testid="holiday-name-input" className="h-9 w-56" placeholder="e.g. Christmas Day" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <Button data-testid="holiday-add-btn" className="rounded-none h-9" onClick={add} disabled={!form.date}>Add holiday</Button>
          <span className="mx-2 text-xs text-muted-foreground">or import a country's holidays:</span>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid="holiday-country-select" className="w-44 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["US", "United States"], ["GB", "United Kingdom"], ["AU", "Australia"], ["NZ", "New Zealand"], ["CA", "Canada"], ["IE", "Ireland"], ["IN", "India"], ["DE", "Germany"], ["FR", "France"], ["ZA", "South Africa"], ["SG", "Singapore"], ["JP", "Japan"]].map(([c, l]) => (
                <SelectItem key={c} value={c}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="holiday-year-select" className="w-24 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0, 1, 2].map((i) => { const y = String(new Date().getFullYear() + i); return <SelectItem key={y} value={y}>{y}</SelectItem>; })}
            </SelectContent>
          </Select>
          <Button data-testid="holiday-import-btn" variant="outline" className="rounded-none h-9" onClick={importCountry} disabled={importing}>
            {importing ? "Importing…" : "Import"}
          </Button>
        </div>
      )}
      <div className="border border-border bg-card">
        {holidays === null ? <div className="p-4 text-sm text-muted-foreground">Loading…</div>
          : holidays.length === 0 ? <div className="p-4 text-sm text-muted-foreground" data-testid="holidays-empty">No holidays yet{isAdmin ? " — add your public holidays above." : "."}</div>
          : holidays.map((h) => (
            <div key={h.date} data-testid="holiday-row" className="flex items-center gap-3 px-4 py-2 border-b border-border last:border-0 text-sm">
              <span className="font-mono2 text-xs">{h.date}</span>
              <span className="flex-1">{h.name}</span>
              {isAdmin && <button data-testid="holiday-delete-btn" onClick={() => remove(h)} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
      </div>
    </div>
  );
}

export default function TeamPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "tenant_admin" || user?.role === "platform_admin";
  const load = () => api.get("/team").then((r) => setMembers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/team", form);
      toast.success(`${form.name} added to the team`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "member" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
      if (e.response?.status === 402) navigate("/billing");
    }
    setBusy(false);
  };

  const changeRole = async (m, role) => {
    try { await api.patch(`/team/${m.id}`, { role }); load(); toast.success("Role updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (m) => {
    try { await api.delete(`/team/${m.id}`); load(); toast.success(`${m.name} removed`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/projects" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /></Link>
            <div className="font-display font-black tracking-tighter text-lg">Ganttastic.app</div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">{user?.tenantName}</span>
            <span className="font-medium">{user?.name}</span>
            <button data-testid="logout-btn" onClick={logout} className="p-1.5 hover:text-red-600"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-8">
        <PageHeader overline={user?.tenantName || "Workspace"} title="Team Members"
          actions={isAdmin && (
            <Button data-testid="add-member-btn" onClick={() => setOpen(true)} className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]">
              <Plus className="h-4 w-4 mr-1" />Add Member
            </Button>
          )}>
          <div className="text-xs text-muted-foreground mt-1">Everyone here shares this tenant's projects — data never crosses tenants.</div>
        </PageHeader>

        <div className="border border-border bg-card overflow-auto">
          <table className="w-full data-table text-sm">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Joined</th><th className="w-16"></th></tr></thead>
            <tbody>
              {members === null ? (
                <tr><td colSpan={5} className="py-6 text-muted-foreground text-center">Loading…</td></tr>
              ) : members.map((m) => (
                <tr key={m.id} data-testid="team-member-row">
                  <td className="font-medium">{m.name}{m.id === user?.id && <span className="text-xs text-muted-foreground ml-1">(you)</span>}</td>
                  <td className="text-muted-foreground">{m.email}</td>
                  <td>
                    {isAdmin && m.id !== user?.id ? (
                      <select data-testid="member-role-select" className="border border-input bg-background px-2 py-1 text-xs"
                        value={m.role} onChange={(e) => changeRole(m, e.target.value)}>
                        <option value="member">member</option>
                        <option value="tenant_admin">tenant_admin</option>
                      </select>
                    ) : <StatusBadge value={m.role} tone="blue" />}
                  </td>
                  <td className="text-muted-foreground text-xs whitespace-nowrap">{(m.createdAt || "").slice(0, 10)}</td>
                  <td>
                    {isAdmin && m.id !== user?.id && (
                      <button data-testid="remove-member-btn" onClick={() => remove(m)} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <HolidaySection isAdmin={user?.role === "tenant_admin" || user?.isPlatformAdmin} />
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-display">Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="member-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Temporary password</Label><Input data-testid="member-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="member-role-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="tenant_admin">Tenant Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="save-member-btn" className="w-full rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={add}
              disabled={busy || !form.name || !form.email || !form.password}>
              {busy ? "Adding…" : "Add member"}
            </Button>
            <p className="text-xs text-muted-foreground">Share the email and temporary password with your teammate — they sign in on the login page.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
